import React from 'react';
import { useLocation } from 'react-router-dom';
import { useFilters } from '../context/FilterContext';
import { X, Filter } from 'lucide-react';
import clsx from 'clsx';

// Pages that ignore the global analytics filters entirely. Showing leftover
// chips there would imply the content is filtered when it isn't — on the Kill
// Switch console that would be dangerous, since an operator could wrongly
// assume products are missing from the list because of a filter.
const NO_FILTER_ROUTES = ['/traffic-controls', '/settings', '/model-testing'];

/**
 * Global filter bar displayed below the TopBar.
 * Shows active filter chips and allows clearing individual/all filters.
 * Parent pages can provide dropdown options via props.
 */
export default function FilterBar({ productOptions, modelOptions, subscriptionOptions }) {
  const { filters, setFilter, clearFilters, hasActiveFilters } = useFilters();
  const location = useLocation();

  if (NO_FILTER_ROUTES.some((r) => location.pathname.startsWith(r))) return null;
  if (!hasActiveFilters) return null;

  const chips = [];
  if (filters.product) chips.push({ key: 'product', label: `Product: ${filters.product}` });
  if (filters.model) chips.push({ key: 'model', label: `Model: ${filters.model}` });
  if (filters.subscription) chips.push({ key: 'subscription', label: `Subscription: ${filters.subscription}` });
  if (filters.user) chips.push({ key: 'user', label: `User: ${filters.user}` });
  if (filters.status) chips.push({ key: 'status', label: `Status: ${filters.status}` });

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 sm:px-6 py-2 bg-sce-green/5 border-b border-sce-green/15">
      <Filter className="w-3.5 h-3.5 text-sce-green flex-shrink-0" />
      <span className="text-xs font-medium text-sce-green flex-shrink-0">Filters:</span>
      <div className="flex items-center gap-1.5 flex-wrap">
        {chips.map((chip) => (
          <span
            key={chip.key}
            className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              'bg-sce-green/10 text-sce-green',
              'ring-1 ring-inset ring-sce-green/20'
            )}
          >
            {chip.label}
            <button
              onClick={() => setFilter(chip.key, '')}
              className="text-sce-green/70 hover:text-sce-green transition"
              aria-label={`Remove ${chip.key} filter`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <button
        onClick={clearFilters}
        className="ml-auto text-xs text-sce-green hover:text-sce-dark transition font-medium"
      >
        Clear All
      </button>
    </div>
  );
}
